import { useState, useMemo } from "react";
import dayjs from "dayjs";
import { BRAND } from "../../types";
import type { CalendarDateItem } from "@/api/tourOrder";

interface DatePickerModalProps {
  open: boolean;
  /** 日历数据列表 */
  calendarList: CalendarDateItem[];
  /** 当前选中的出发日期 */
  selectedDate: string;
  /** 行程天数 */
  days: number;
  /** 行程晚数 */
  nights: number;
  onClose: () => void;
  onConfirm: (date: string) => void;
}

const WEEK_DAYS = ["日", "一", "二", "三", "四", "五", "六"];

export function DatePickerModal({
  open,
  calendarList,
  selectedDate,
  days,
  nights,
  onClose,
  onConfirm,
}: DatePickerModalProps) {
  const [currentMonth, setCurrentMonth] = useState(() => dayjs(selectedDate || undefined));
  const [tempDate, setTempDate] = useState(selectedDate || "");

  // 将 calendarList 转为 Map 方便查询
  const calendarMap = useMemo(() => {
    const map = new Map<string, CalendarDateItem>();
    calendarList.forEach((item) => map.set(item.date, item));
    return map;
  }, [calendarList]);

  // 生成当前月份的日历格子
  const calendarDays = useMemo(() => {
    const startOfMonth = currentMonth.startOf("month");
    const endOfMonth = currentMonth.endOf("month");
    const startDayOfWeek = startOfMonth.day(); // 0=周日
    const daysInMonth = endOfMonth.date();

    const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];

    // 前面补空
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const d = startOfMonth.subtract(startDayOfWeek - i, "day");
      cells.push({ day: d.date(), dateStr: d.format("YYYY-MM-DD"), isCurrentMonth: false });
    }
    // 当月日期
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = currentMonth.date(d).format("YYYY-MM-DD");
      cells.push({ day: d, dateStr, isCurrentMonth: true });
    }
    // 后面补满6行
    const remaining = 42 - cells.length;
    for (let i = 1; i <= remaining; i++) {
      const d = endOfMonth.add(i, "day");
      cells.push({ day: d.date(), dateStr: d.format("YYYY-MM-DD"), isCurrentMonth: false });
    }

    return cells;
  }, [currentMonth]);

  // 计算返回日期
  const returnDateStr = tempDate ? dayjs(tempDate).add(days - 1, "day").format("YYYY-MM-DD") : "";

  // 判断日期是否可选
  function isDateAvailable(dateStr: string): boolean {
    // 已过期的不可选
    if (dayjs(dateStr).isBefore(dayjs().startOf("day"))) return false;
    // 无日历数据时，未来日期默认可选
    if (calendarList.length === 0) return true;
    const item = calendarMap.get(dateStr);
    if (!item) return true; // 日历数据不包含的日期，默认可选
    return item.is_available;
  }

  function handleSelect(dateStr: string) {
    if (!isDateAvailable(dateStr)) return;
    setTempDate(dateStr);
  }

  function handlePrevMonth() {
    setCurrentMonth((m) => m.subtract(1, "month"));
  }

  function handleNextMonth() {
    setCurrentMonth((m) => m.add(1, "month"));
  }

  function handleConfirm() {
    if (tempDate && isDateAvailable(tempDate)) {
      onConfirm(tempDate);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center">
      {/* 遮罩 */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      {/* 弹窗主体 */}
      <div
        className="relative w-full max-w-lg mx-auto rounded-t-3xl overflow-hidden"
        style={{ background: "#fff", maxHeight: "90vh" }}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F3F4F6" }}
          >
            <i className="ri-close-line text-gray-500" style={{ fontSize: 18 }} />
          </button>
          <div className="text-base font-bold text-gray-900">选择出行日期</div>
          <button
            onClick={handleConfirm}
            className="px-4 py-1.5 rounded-full text-white text-sm font-medium cursor-pointer"
            style={{ background: BRAND }}
          >
            确定
          </button>
        </div>

        {/* 月份导航 */}
        <div className="flex items-center justify-between px-5 py-3">
          <button
            onClick={handlePrevMonth}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F3F4F6" }}
          >
            <i className="ri-arrow-left-s-line text-gray-600" style={{ fontSize: 20 }} />
          </button>
          <div className="text-base font-bold text-gray-900">
            {currentMonth.format("YYYY年M月")}
          </div>
          <button
            onClick={handleNextMonth}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F3F4F6" }}
          >
            <i className="ri-arrow-right-s-line text-gray-600" style={{ fontSize: 20 }} />
          </button>
        </div>

        {/* 星期头 */}
        <div className="grid grid-cols-7 px-3 pb-2">
          {WEEK_DAYS.map((wd, i) => (
            <div
              key={wd}
              className="text-center text-xs font-medium py-1"
              style={{
                color: (i === 0 || i === 6) ? BRAND : "#9CA3AF",
              }}
            >
              {wd}
            </div>
          ))}
        </div>

        {/* 日期网格 */}
        <div className="grid grid-cols-7 px-3 gap-y-1">
          {calendarDays.map((cell) => {
            const available = isDateAvailable(cell.dateStr);
            const selected = cell.dateStr === tempDate;
            const isPast = dayjs(cell.dateStr).isBefore(dayjs().startOf("day")) && !cell.isCurrentMonth;
            const isToday = cell.dateStr === dayjs().format("YYYY-MM-DD");

            return (
              <button
                key={cell.dateStr}
                onClick={() => handleSelect(cell.dateStr)}
                disabled={!available}
                className="relative flex items-center justify-center h-10 rounded-full cursor-pointer transition-all"
                style={{
                  background: selected ? BRAND : "transparent",
                  color: selected
                    ? "#fff"
                    : !cell.isCurrentMonth
                      ? "#D1D5DB"
                      : available
                        ? "#374151"
                        : "#E5E7EB",
                  opacity: (!cell.isCurrentMonth || (!available && !selected)) ? 0.35 : 1,
                  fontWeight: selected ? 700 : 400,
                  fontSize: 14,
                }}
              >
                {cell.day}
                {isToday && !selected && (
                  <span
                    className="absolute bottom-1 w-1 h-1 rounded-full"
                    style={{ background: BRAND }}
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* 底部已选信息 */}
        {tempDate && (
          <div className="mx-4 my-4 p-3.5 rounded-xl" style={{ background: "#FFF7ED", border: "1px solid #FFEDD5" }}>
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: BRAND }}
              >
                <i className="ri-calendar-check-line text-white" style={{ fontSize: 16 }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-gray-500 mb-0.5">已选出行日期</div>
                <div className="text-sm font-bold text-gray-900">
                  {dayjs(tempDate).format("YYYY年MM月DD日")} 出发
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  返回日期：{returnDateStr} · 共{days}天{nights}晚
                </div>
              </div>
              {/* 麦克风图标装饰 */}
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: BRAND }}
              >
                <i className="ri-mic-line text-white" style={{ fontSize: 18 }} />
              </div>
            </div>
          </div>
        )}

        {/* 图例 */}
        <div className="flex items-center gap-5 px-4 pb-5 pt-1">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: BRAND }} />
            <span className="text-xs text-gray-500">已选日期</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: "#E5E7EB" }} />
            <span className="text-xs text-gray-500">不可选（已过）</span>
          </div>
        </div>
      </div>
    </div>
  );
}
